import React, { useState } from 'react';
import { Database, Server, Play, RefreshCw } from 'lucide-react';
import { useClient } from '@/contexts/ClientContext';

// Unified SSMS-style SQL Workspace
export default function SQLWorkspace() {
  type QueryResult = {
    columns: string[];
    rows: any[][];
  } | null;

  const { selectedClient } = useClient();
  const [connected, setConnected] = useState(false);
  const [serverFQDN, setServerFQDN] = useState('');
  const [databaseName, setDatabaseName] = useState('');
  const [connectionString, setConnectionString] = useState('');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const [query, setQuery] = useState('SELECT 1');
  const [results, setResults] = useState<QueryResult>(null);
  const [loading, setLoading] = useState(false);
  const [lastPayload, setLastPayload] = useState<any>(null);
  const [lastResponse, setLastResponse] = useState<any>(null);

  // Safe response parser to avoid JSON parse errors when server returns HTML
  const safeParseResponse = async (resp: Response) => {
    const ct = resp.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      try {
        return await resp.json();
      } catch (e) {
        // Fall through to text
      }
    }
    try {
      const text = await resp.text();
      return { __rawText: text } as any;
    } catch (e) {
      return { __rawText: '' } as any;
    }
  };

  // Call backend test-connection endpoint
  const testConnection = async () => {
    setConnectionError(null);
    if (!selectedClient) {
      setConnectionError('No client selected');
      return { isConnected: false };
    }

    const payload = {
      ServerFQDN: serverFQDN,
      DatabaseName: databaseName,
      ConnectionString: connectionString
    };

    try {
      setLoading(true);
      const resp = await fetch(`/api/clients/${selectedClient.id}/sql-management/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      // Debug: record payload sent
      setLastPayload(payload);
      console.log('TestConnection payload:', payload);

  const parsed = await safeParseResponse(resp);
  // Debug: record response
  setLastResponse(parsed);
  console.log('TestConnection response:', parsed);
      setLoading(false);

      if (!resp.ok) {
        const details = parsed?.Details || parsed?.ErrorMessage || parsed?.error || parsed?.__rawText || resp.statusText;
        setConnectionError(`HTTP ${resp.status}: ${String(details).slice(0,400)}`);
        setConnected(false);
        return { isConnected: false };
      }

      // If server returned raw HTML/text, show a snippet for debugging
      if (parsed?.__rawText) {
        // If it looks like HTML, show friendly message
        const snippet = parsed.__rawText.slice(0, 400);
        setConnected(false);
        setConnectionError(`Unexpected non-JSON response: ${snippet}`);
        return { isConnected: false };
      }

      const json = parsed;

      // The API returns { IsConnected, ServerVersion, DatabaseCollation, ... }
      if (json?.IsConnected) {
        setConnected(true);
        setServerVersion(json?.ServerVersion ?? null);
        setConnectionError(null);
      } else {
        setConnected(false);
        setConnectionError(json?.ErrorMessage ?? 'Unable to connect');
      }

      return json;
    } catch (err: any) {
      setLoading(false);
      setConnected(false);
      setConnectionError(err?.message || String(err));
      return { isConnected: false };
    }
  };

  const handleConnect = async () => {
    await testConnection();
  };

  const handleDisconnect = () => {
    setConnected(false);
    setServerVersion(null);
  };

  // Run query via backend execute endpoint
  const runQuery = async () => {
    if (!selectedClient) return;
    if (!connected) {
      setConnectionError('Not connected to a server');
      return;
    }

    const payload = {
      ServerFQDN: serverFQDN,
      DatabaseName: databaseName,
      ConnectionString: connectionString,
      SqlCommand: query,
      TimeoutSeconds: 30
    };

    try {
      setLoading(true);
      const resp = await fetch(`/api/clients/${selectedClient.id}/sql-management/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      // Debug: record payload/response
      setLastPayload(payload);
      console.log('Execute payload:', payload);

      const parsed = await safeParseResponse(resp);
      setLastResponse(parsed);
      console.log('Execute response:', parsed);
      setLoading(false);

      if (!resp.ok) {
        const details = parsed?.Details || parsed?.ErrorMessage || parsed?.error || parsed?.__rawText || resp.statusText;
        setConnectionError(`HTTP ${resp.status}: ${String(details).slice(0,400)}`);
        setResults(null);
        return;
      }

      if (parsed?.__rawText) {
        setConnectionError(`Unexpected non-JSON response: ${parsed.__rawText.slice(0,400)}`);
        setResults(null);
        return;
      }

      const json = parsed;

      if (json?.Success && Array.isArray(json?.Results)) {
        const rows = json.Results as Array<Record<string, any>>;
        const columns = rows.length > 0 ? Object.keys(rows[0]) : ['Result'];
        const dataRows = rows.map(r => columns.map(c => r[c]));
        setResults({ columns, rows: dataRows });
      } else if (json?.Success && typeof json?.RowsAffected === 'number') {
        setResults({ columns: ['RowsAffected'], rows: [[json.RowsAffected]] });
      } else {
        setResults(null);
        setConnectionError(json?.ErrorMessage || 'No results');
      }
    } catch (err: any) {
      setLoading(false);
      setConnectionError(err?.message || String(err));
    }
  };

  return (
    <div>
      {/* Client Context Header */}
      {selectedClient && (
        <div className="bg-shiftleft-50 border-b border-shiftleft-200 p-4 mb-4">
          <div className="flex items-center gap-4">
            {selectedClient.logoBase64 || selectedClient.logoUrl ? (
              <img
                src={selectedClient.logoBase64 || selectedClient.logoUrl}
                alt={`${selectedClient.name} Logo`}
                className="w-10 h-10 object-contain border border-gray-200 rounded-lg bg-white p-1"
              />
            ) : (
              <div className="w-10 h-10 bg-shiftleft-600 rounded-lg flex items-center justify-center text-white font-bold">
                {selectedClient.code.charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">SQL Management - {selectedClient.name}</h2>
              <p className="text-sm text-gray-600">
                {selectedClient.warehouseSystem} • {selectedClient.databaseDialect} • {selectedClient.location}
              </p>
            </div>
            <div className="ml-auto">
              <span className="text-xs bg-shiftleft-100 text-shiftleft-800 px-2 py-1 rounded">
                Client: {selectedClient.code}
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div style={{ background: '#ff0', color: '#000', padding: '8px', fontWeight: 'bold', textAlign: 'center', zIndex: 9999 }}>
        SQLWorkspace Rendered (Debug Banner)
      </div>
      <div className="flex h-[80vh] border rounded-lg overflow-hidden shadow bg-white dark:bg-gray-900">
        {/* Sidebar: Explorer */}
        <div className="w-64 border-r bg-gray-50 dark:bg-gray-800 p-3 flex flex-col">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-shiftleft-600" />
            <div className="flex flex-col">
              <span className="font-bold text-gray-900 dark:text-white">SQL Explorer</span>
              {selectedClient && (
                <span className="text-xs text-gray-600 dark:text-gray-400">{selectedClient.code}</span>
              )}
            </div>
          </div>
          <div className="mb-3">
            <input
              className="w-full mb-2 px-2 py-1 border rounded text-sm"
              placeholder="Server FQDN (e.g. sqlhost.example.com)"
              value={serverFQDN}
              onChange={e => setServerFQDN(e.target.value)}
            />
            <input
              className="w-full mb-2 px-2 py-1 border rounded text-sm"
              placeholder="Database name (optional)"
              value={databaseName}
              onChange={e => setDatabaseName(e.target.value)}
            />
            <input
              className="w-full mb-2 px-2 py-1 border rounded text-sm"
              placeholder="Full connection string (optional)"
              value={connectionString}
              onChange={e => setConnectionString(e.target.value)}
            />
          </div>

          {/* Debug: show current serverFQDN and last payload/response in dev */}
          <div className="text-xs text-gray-500 mt-2">
            <div>Current ServerFQDN: <span className="font-mono">{serverFQDN || '<empty>'}</span></div>
            {lastPayload && (
              <div className="mt-1">Last payload: <pre className="whitespace-pre-wrap max-h-24 overflow-auto">{JSON.stringify(lastPayload, null, 2)}</pre></div>
            )}
            {lastResponse && (
              <div className="mt-1">Last response: <pre className="whitespace-pre-wrap max-h-24 overflow-auto">{JSON.stringify(lastResponse, null, 2)}</pre></div>
            )}
          </div>

          <button
            className="mb-2 px-3 py-1 bg-shiftleft-600 text-white rounded hover:bg-shiftleft-700"
            onClick={connected ? handleDisconnect : handleConnect}
          >
            {connected ? 'Disconnect' : 'Connect'}
          </button>
          {connectionError && (
            <div className="text-sm text-red-600 mt-2">Error: {connectionError}</div>
          )}
          {serverVersion && (
            <div className="text-xs text-gray-600 mt-2">Server: {serverVersion}</div>
          )}
          {/* Static Analysis Dashboard Link */}
          <a
            // href removed: static analysis now only in client dashboard
            className="mb-3 block px-3 py-2 bg-yellow-400 text-black font-semibold rounded hover:bg-yellow-500 text-center transition-colors"
            style={{ textDecoration: 'none' }}
            tabIndex={0}
          >
            Static Sproc Analysis
          </a>
          {/* Placeholder for tree view */}
          <div className="flex-1 overflow-y-auto text-sm text-gray-700 dark:text-gray-200">
            {connected ? (
              <ul>
                <li className="font-semibold">Databases</li>
                <ul className="ml-4">
                  <li>master</li>
                  <li>tempdb</li>
                  <li>model</li>
                  <li>msdb</li>
                </ul>
              </ul>
            ) : (
              <div className="text-gray-400 mt-8">Not connected</div>
            )}
          </div>
        </div>
        {/* Main: Query Editor and Results */}
        <div className="flex-1 flex flex-col">
          {/* Top Bar: Query Editor */}
          <div className="flex items-center border-b bg-gray-100 dark:bg-gray-800 p-2 gap-2">
            <textarea
              className="flex-1 p-2 rounded border text-sm font-mono bg-white dark:bg-gray-900 dark:text-white"
              rows={3}
              value={query}
              onChange={e => setQuery(e.target.value)}
              disabled={!connected}
              placeholder="Write your SQL query here..."
            />
            <button
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1"
              onClick={runQuery}
              disabled={!connected || loading}
            >
              <Play className="w-4 h-4" /> Run
            </button>
            {/* Clear Query Button (no Stop icon in lucide-react) */}
            <button
              className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={() => setQuery('')}
              disabled={!connected || loading}
            >
              Clear
            </button>
            <button
              className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              onClick={() => setResults(null)}
              disabled={!connected || loading}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          {/* Results Panel */}
          <div className="flex-1 overflow-auto p-4 bg-white dark:bg-gray-900">
            {loading && <div className="text-shiftleft-600">Running query...</div>}
            {results && Array.isArray(results.columns) && Array.isArray(results.rows) && (
              <table className="min-w-full text-sm border mt-2">
                <thead>
                  <tr>
                    {results.columns.map((col: string) => (
                      <th key={col} className="border-b px-2 py-1 text-left bg-gray-50 dark:bg-gray-800">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {results.rows.map((row: any[], i: number) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="border-b px-2 py-1">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!results && !loading && (
              <div className="text-gray-400 mt-8">No results to display.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
